/**
 * A visit as a list row — **one row per visit** (Aaron, Oct 30: *"Visits is
 * one record that spans the multiple days"*; `docs/walkthrough-findings-2026-10-30.md`
 * §1). The Visits list (`pages/visits.tsx`) and every record's Visits tab
 * (`related-list.tsx`'s `visits` preset) draw a visit from these, so the row
 * on a job's tab and on the Visits screen are the same row.
 *
 * The columns are folder 32 §3.1's Job › Visits list (`visits-01`): **Visit**
 * (the name; under it V-#### and the series chip *26 of 30*, or *one-off*, or
 * a multi-day one-off's *Oct 26 – Nov 7 · 5 days* — never a day's index) ·
 * **Date** (the first day; *not scheduled · from Apr 15* with none) · **Days**
 * (*3 days · Okafor 9 + 9 · Okafor & Reyes 6*) · **Crew** · **Status** (§17.1's,
 * the visit's — the Visit page's own chip) · **Est h** · **Actual** ·
 * **Invoice**, plus Job and Property. The mark is the visit's calendar card
 * (`list-marks.md` §2) — the Visit page's own `VisitMark`, at 28: the second
 * card behind it for a multi-day visit, the loop badge for a series.
 *
 * A row opens `/visits/<work item id>`. Nothing here creates a visit: visits
 * are made only from an approved quote (`docs/visit-page.md` §26).
 */
import type { ReactNode } from 'react'
import { VISIT_PAGE_STATUSES, type VisitMarkData, type VisitPageStatus } from '@rowted/shared'
import { Avatar, StatusChip } from './primitives.js'
import type { CardLine } from './data-list.js'
import type { MenuItem } from './context-menu.js'
import { lastActivity, text } from './card-line.js'
import { VisitMark } from '../visit/visit-mark.js'
import { dayRange, dayText, hours1 } from '../visit/format.js'
import { HEADER } from '../visit/copy.js'
import { copyItem, openItems } from '../lib/row-menu.js'
import { EM_DASH } from '../lib/format.js'
import type { ListRow } from '../lib/use-list.js'

/** The board's words (folder 32 `visits-01`; walkthrough Oct 30 §1). */
const WORDS = {
  visit: 'Visit',
  oneOff: 'one-off',
  /** *26 of 30*. */
  seriesAt: (index: number, count: number | null) => `${index} of ${count ?? '∞'}`,
  /** *Oct 26 – Nov 7 · 5 days*. */
  span: (range: string, days: number) => `${range} · ${days} days`,
  /** *not scheduled · from Apr 15*. */
  notScheduled: (from: string | null) => (from ? `not scheduled · from ${from}` : 'not scheduled'),
  today: 'today',
  soFar: (hours: string) => `${hours} so far`,
  notYet: 'not yet',
  menu: {
    copyVisit: 'Copy visit number',
    copyJob: 'Copy job number',
    openJob: 'Open the job',
    openProperty: 'Open the property',
  },
} as const

/** Read on every row whatever the view's columns are — the mark, the two lines, the cells and the ⋯. */
export const VISIT_ROW_FIELDS = [
  'reference', 'description', 'seriesIndex', 'seriesCount', 'scheduledDate', 'lastDay', 'earliestOn',
  'dayCount', 'days', 'status', 'crewName', 'crewLeadName', 'jobId', 'jobReference', 'jobTitle',
  'propertyId', 'invoiceStatus', 'orgUnitName', 'lastActivityAt',
]

const num = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

function statusOf(row: ListRow): VisitPageStatus {
  const status = text(row.status)
  return (VISIT_PAGE_STATUSES as readonly string[]).includes(status) ? status as VisitPageStatus : 'scheduled'
}

/** A series occurrence — the count is null when the series is open-ended; *1 of 1* is a one-off. */
function seriesOf(row: ListRow): { index: number; count: number | null } | null {
  const index = num(row.seriesIndex)
  const count = num(row.seriesCount)
  if (index === null) return null
  return count === null || count > 1 ? { index, count } : null
}

const dayOf = (value: unknown): string | null => {
  const day = text(value).slice(0, 10)
  return /^\d{4}-\d{2}-\d{2}$/.test(day) ? day : null
}

/** Today where the reader is, as `YYYY-MM-DD` — the Days column's *· today*. */
function todayHere(): string {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
}

// ---------------------------------------------------------------------------
// The mark
// ---------------------------------------------------------------------------

export function visitMarkData(row: ListRow): VisitMarkData {
  return {
    date: dayOf(row.scheduledDate),
    status: statusOf(row),
    multiDay: (num(row.dayCount) ?? 0) > 1,
    series: seriesOf(row) !== null,
  }
}

export function visitRowMark(row: ListRow, size: 28 | 44 | 56 = 28): ReactNode {
  return <VisitMark mark={visitMarkData(row)} size={size} />
}

// ---------------------------------------------------------------------------
// The Visit column: the name, and under it V-#### · the series chip
// ---------------------------------------------------------------------------

/** The board's series chip — the loop and *26 of 30*, indigo on its tint. */
function SeriesChip({ index, count }: { index: number; count: number | null }) {
  return (
    <span
      className="inline-flex h-4 items-center gap-1 rounded-[5px] px-[5px] align-[-2px] text-[10.5px] font-bold tabular-nums"
      style={{ color: 'rgb(var(--color-indigo))', background: 'rgb(var(--status-job-reviewed-bg))' }}
      data-series-chip=""
    >
      <svg viewBox="0 0 24 24" width={9} height={9} fill="none" stroke="currentColor" strokeWidth={2.6}
        strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M4 12a8 8 0 1 0 3-6.2L4 8M4 3v5h5" />
      </svg>
      {WORDS.seriesAt(index, count)}
    </span>
  )
}

/** The second line: *V-1187 · ↻ 26 of 30*, *V-1188 · one-off*, *V-1193 · Oct 26 – 27 · 2 days*. */
export function visitSecondLine(row: ListRow): ReactNode {
  const reference = text(row.reference)
  const series = seriesOf(row)
  const first = dayOf(row.scheduledDate)
  const last = dayOf(row.lastDay)
  const days = num(row.dayCount) ?? 0
  const after: ReactNode = series
    ? <SeriesChip index={series.index} count={series.count} />
    : days > 1 && first && last
      ? WORDS.span(dayRange(first, last, 'short'), days)
      : WORDS.oneOff
  return (
    <>
      {reference}
      {reference ? ' · ' : ''}
      {after}
    </>
  )
}

export const VISIT_PRIMARY = {
  field: 'description',
  header: WORDS.visit,
  width: 240,
  render: (row: ListRow) => ({
    primary: text(row.description) || EM_DASH,
    secondary: visitSecondLine(row),
  }),
}

// ---------------------------------------------------------------------------
// The cells
// ---------------------------------------------------------------------------

const faint = (value: ReactNode = EM_DASH) => <span className="text-text-faint">{value}</span>

/** A chip with no dot — the board's Invoice cell. */
function PlainChip({ fg, bg, children }: { fg: string; bg: string; children: ReactNode }) {
  return (
    <span
      className="inline-flex h-5 items-center rounded-[5px] px-[7px] text-[11px] font-semibold"
      style={{ color: fg, background: bg }}
    >
      {children}
    </span>
  )
}

export const VISIT_CELLS: Record<string, (row: ListRow) => ReactNode> = {
  scheduledDate: (row) => {
    const first = dayOf(row.scheduledDate)
    if (first) return <span className="font-semibold">{dayText(first, 'date')}</span>
    const from = dayOf(row.earliestOn)
    return <span className="text-text-muted">{WORDS.notScheduled(from ? dayText(from, 'monthDay') : null)}</span>
  },
  days: (row) => {
    const words = text(row.days)
    if (!words) return faint()
    const today = (num(row.dayCount) ?? 0) === 1 && dayOf(row.scheduledDate) === todayHere()
    return <span className="text-text-muted">{today ? `${words} · ${WORDS.today}` : words}</span>
  },
  crewName: (row) => {
    const crew = text(row.crewName)
    if (!crew) return faint()
    return (
      <span className="inline-flex min-w-0 items-center gap-1.5">
        <Avatar name={text(row.crewLeadName) || crew} size={22} />
        <span className="truncate">{crew}</span>
      </span>
    )
  },
  status: (row) => {
    const status = statusOf(row)
    return <StatusChip group="visit" status={status}>{HEADER.status[status]}</StatusChip>
  },
  budgetedHours: (row) => {
    const hours = num(row.budgetedHours)
    return hours === null ? faint() : <span className="tabular-nums">{hours1(hours)}</span>
  },
  actualHours: (row) => {
    const hours = num(row.actualHours)
    if (hours === null) return faint()
    return (
      <span className="tabular-nums">
        {statusOf(row) === 'in_progress' ? WORDS.soFar(hours1(hours)) : hours1(hours)}
      </span>
    )
  },
  invoiceReference: (row) => {
    const reference = text(row.invoiceReference)
    if (reference) {
      const status = text(row.invoiceStatus) || 'draft'
      return (
        <PlainChip fg={`rgb(var(--status-invoice-${status}-fg))`} bg={`rgb(var(--status-invoice-${status}-bg))`}>
          {reference}
        </PlainChip>
      )
    }
    const none = statusOf(row) === 'skipped' || statusOf(row) === 'cancelled'
    return (
      <PlainChip fg="rgb(var(--color-text-faint))" bg="rgb(var(--color-hover))">
        {none ? EM_DASH : WORDS.notYet}
      </PlainChip>
    )
  },
}

/** Widths for the columns the generic rule sizes badly. */
export const VISIT_COLUMN_WIDTHS: Record<string, number> = {
  scheduledDate: 150, days: 230, crewName: 170, status: 124, budgetedHours: 80, actualHours: 96,
  invoiceReference: 110,
}

// ---------------------------------------------------------------------------
// The phone card's third row and the row's ⋯
// ---------------------------------------------------------------------------

/** *[In progress] Harrisburg — Oct 22* (v3 §8): the visit's chip, the branch, the last activity. */
export function visitCardLine(row: ListRow): CardLine {
  return {
    chip: VISIT_CELLS.status!(row),
    facts: [text(row.orgUnitName) || null].filter((part) => part !== null),
    trailing: lastActivity(row),
  }
}

/** **Open · Copy visit number · Copy job number · Open the job · Open the property** (walkthrough Oct 30 §1). */
export function visitRowMenu(row: ListRow, navigate: (to: string) => void): MenuItem[] {
  const items: MenuItem[] = [
    ...openItems(`/visits/${String(row.id)}`, navigate),
    ...copyItem('copy-visit', WORDS.menu.copyVisit, row.reference, true),
    ...copyItem('copy-job', WORDS.menu.copyJob, row.jobReference, !text(row.reference)),
  ]
  if (text(row.jobId)) {
    items.push({
      key: 'job', label: WORDS.menu.openJob, separatorBefore: true,
      onSelect: () => navigate(`/jobs/${String(row.jobId)}`),
    })
  }
  if (text(row.propertyId)) {
    items.push({
      key: 'property', label: WORDS.menu.openProperty, separatorBefore: !text(row.jobId),
      onSelect: () => navigate(`/properties/${String(row.propertyId)}`),
    })
  }
  return items
}
